import {
	Badge,
	Box,
	Button,
	Heading,
	HStack,
	StepIcon,
	StepIndicator,
	StepNumber,
	Stepper,
	StepSeparator,
	StepStatus,
	useMediaQuery,
	useSteps
} from "@chakra-ui/react";
import { cloneDeep } from "lodash";
import { FunctionComponent, ReactElement, useEffect, useMemo, useRef } from "react";
import { HiChevronLeft } from "react-icons/hi";
import { generatePath, Location, useLocation, useNavigate, useParams } from "react-router-dom";

// Components
import LayoutWrapper from "../../shared/layout";
import LoadingSpinner from "../../shared/LoadingSpinner/LoadingSpinner";

// Hooks
import { useAuth } from "../../hooks/useAuth";
import usePermission from "../../hooks/usePermission";

// Sub Components
import AssociateDocs from "./steps/AssociateDocs";
import BasicInfo from "./steps/BasicInfo";
import Billing from "./steps/Billing";
import Confirmation from "./steps/Confirmation";
import Services from "./steps/Services";

// Context
import { useClaim } from "../../context-providers/claims-provider";

// Styles
import { Page } from "../../shared/layout/index.styles";
import { SpecialStep, StepInfoDescription, StepInfoTitle, StepInfoWrapper } from "./NewClaim.styles";

// Utils
import { RouterPathMapping } from "../../Router";
import { ClaimStatus, DocumentCategory, UserRole } from "../../utils/consts";
import { Claim, ClaimServiceBillingType, User } from "../../utils/types";

/**
 * Defined Steps mapper for the Chakra Stepper
 * For more information, see: https://v2.chakra-ui.com/docs/components/stepper
 */
const steps = [
	{ label: "Claim Information", description: "Basic Claim-Related Information" },
	{ label: "Services", description: "Services Performed for the Claim" },
	{ label: "Documents", description: "Associate Documents to Services" },
	{ label: "Billing", description: "Billing Specific to each Service" },
	{ label: "Confirmation", description: "Confirm Claim Details" }
];

/**
 * NewClaim page responsible for processing a new Claim object.
 * Subsequent "steps" are handled as child components.
 */
const NewClaim: FunctionComponent = (): ReactElement => {
	const { isLoading, claim, localClaim, handleFetchClaim, handleSubmitClaim, handleClaimUpsert } = useClaim();
	const { activeStep, setActiveStep } = useSteps({
		index: -1,
		count: steps.length
	});
	// Hooks
	const { id, caseId } = useParams();
	const isFirstPass = useRef(true);
	const { hasRole } = useAuth();
	const navigate = useNavigate();
	const { state, pathname } = useLocation() as Location<{ from: string }>;
	const isAttorney = hasRole([UserRole.Provider]);
	const { getUser } = useAuth();
	const { hasGeneralAdminAccess } = usePermission();
	const [isMobile] = useMediaQuery("(max-width: 800px)");

	useEffect(() => {
		if (claim?.status !== ClaimStatus.Draft) {
			navigate(`/claim/${claim.claimId}/detail`);
		}
	}, [claim?.status, claim?.claimId, navigate]);

	useEffect(() => {
		// After claim is created, navigate to the specific claim intake page
		if (pathname.includes("/attorney/case/") && caseId && claim.claimId) {
			const claimIntake = generatePath(RouterPathMapping.Attorney_ClaimIntake, {
				id: claim.claimId.toString()
			});
			navigate(claimIntake, { state: state });
		}
		if (pathname.includes(RouterPathMapping.ClaimCreate) && claim.claimId) {
			const claimIntake = generatePath(RouterPathMapping.ClaimIntake, {
				id: claim.claimId.toString()
			});
			navigate(claimIntake, { state: state });
		}
	}, [claim]);

	const handleStepClick = (index: number): void => setActiveStep(index);

	/** Navigate to a step and save as well */
	const handleSaveStepClick = async (index: number) => {
		// If the current step is Billing (step index 2), navigate without saving
		if (activeStep === 2) {
			setActiveStep(index);
			return;
		}
		// Handle invalid local claim data so that the server doesn't error
		const toSubmitClaim = cloneDeep(localClaim);

		// Inject any missing billing values with 0
		for (const service of toSubmitClaim.claimServices) {
			if (service.billing) {
				for (const billingItem of service.billing) {
					if (!billingItem.discountedRate) {
						billingItem.discountedRate = "0.00";
					}
					if (!billingItem.standardRate) {
						billingItem.standardRate = "0.00";
					}
					if (
						billingItem.type !== ClaimServiceBillingType.Flat &&
						billingItem.type !== ClaimServiceBillingType.FlatHigh &&
						!billingItem.hours
					) {
						billingItem.hours = "0.00";
					}
				}
			}
		}
		await handleClaimUpsert(toSubmitClaim, false);
		setActiveStep(index);
	};

	const handlePreviousClick = (): void => handleStepClick(activeStep - 1);

	const handleEditStep = (index: number): void => setActiveStep(index);

	/** This function determines which step should be shown initially when opening a claim */
	const firstInvalidStep = (claim: Claim | undefined): number => {
		// Step 1 validation, ensure there is basic info, like an id, attorney, case, and first & last dates
		if (
			!claim ||
			!claim.claimId ||
			!claim.atty ||
			!claim.caseId ||
			!claim.dateOfInitialConsultation ||
			!claim.finalDateOfService
		) {
			return 0;
		}
		// Step 2 validation, ensure there is at least one service and that all services have the required fields
		else if (
			!claim.claimServices.length ||
			claim.claimServices.some((service) => !service.serviceId || !service.serviceProvidedFor)
		) {
			return 1;
		}
		// Step 3 validation, ensure that for roles less than admin, documents for each required category have been uploaded (Require a Claim Form OR CCS Form)
		else if (
			!hasGeneralAdminAccess &&
			(!claim.documents.length ||
				!(
					claim.documents.some((document) => document.category === DocumentCategory.Invoices && !document.isDeleted) &&
					(claim.documents.some(
						(document) => document.category === DocumentCategory.ClaimForms && !document.isDeleted
					) ||
						claim.documents.some((document) => document.category === DocumentCategory.Ccs && !document.isDeleted))
				))
		) {
			return 2;
		}
		// Step 4 validation, ensure that each service has at least one billing item and that all billing items have the required fields
		else if (
			claim.claimServices.some(
				(service) =>
					!service.billing?.length ||
					service.billing.some((billing) => {
						let currentUser: User | undefined = getUser();
						if (currentUser) {
							if (currentUser.role.split(",").indexOf(UserRole.ClaimsAnalyst) === -1) {
								return (
									!billing.discountedRate ||
									parseFloat(billing.discountedRate) <= 0 ||
									!billing.standardRate ||
									parseFloat(billing.standardRate) <= 0 ||
									(billing.type !== ClaimServiceBillingType.Flat &&
										billing.type !== ClaimServiceBillingType.FlatHigh &&
										(!billing.hours || parseFloat(billing.hours) <= 0))
								);
							} else {
								return (
									!billing.discountedRate ||
									parseFloat(billing.discountedRate) <= 0 ||
									(billing.type !== ClaimServiceBillingType.Flat &&
										billing.type !== ClaimServiceBillingType.FlatHigh &&
										(!billing.hours || parseFloat(billing.hours) <= 0))
								);
							}
						} else return true;
					})
			)
		) {
			return 3;
		}
		// Otherwise return confirmation step 5
		else {
			return 4;
		}
	};

	useEffect(() => {
		// On first load of an existing claim, fetch the claim and set the active step
		const existingClaimsCheck = async () => {
			isFirstPass.current = false;
			if (id)
				await handleFetchClaim(parseInt(id)).then((claim) => {
					setActiveStep(firstInvalidStep(claim));
				});
		};

		if (id && !claim.claimId?.toString()) {
			if (isFirstPass.current) {
				existingClaimsCheck();
			}
		} else {
			if (activeStep < 0) setActiveStep(0);
		}

		existingClaimsCheck();
	}, []);

	/** The index of the first invalid step */
	const firstInvalidStepIndex = useMemo(() => {
		return firstInvalidStep(claim);
	}, [claim]);

	return (
		<>
			{isLoading && <LoadingSpinner />}
			<LayoutWrapper>
				<Box width="100%" height="100%">
					<Box mx={8}>
						<Page>
							<Box overflowY="auto" overflowX="auto">
								<HStack borderBottom="2px solid" borderColor="chakra-border-color" pb="3">
									<Button
										leftIcon={<HiChevronLeft />}
										variant="ghost"
										onClick={() =>
											navigate(
												state?.from ??
													(isAttorney ? RouterPathMapping.AttorneyClaims : RouterPathMapping.ClaimsIndexDefault)
											)
										}>
										{" "}
										Back
									</Button>
									<Heading fontSize={20} flex={1} textAlign={"center"} paddingRight="100px">
										New Claim {claim.claimId ? "#" + claim.claimId : ""}{" "}
										{claim.caseId ? "for Case #" + claim.caseId : ""}
										{claim.atty ? (claim.caseId ? ", " : "for ") : ""}
										{claim.atty ? "Attorney #" + claim.atty.getId() : ""}
									</Heading>
									<Badge
										fontSize="sm"
										variant="outline"
										fontWeight={500}
										textTransform="none"
										borderRadius="4px"
										mr={3}>
										Draft
									</Badge>
								</HStack>

								<Box display="flex" flexDirection="row" justifyContent={"space-evenly"}>
									<Box data-testid="new-claim-wrapper" display="flex" padding="10px" flex={1}>
										{/* Mobile Stepper (Horizontal) */}
										{isMobile && (
											<Box display="flex" flexDirection="column" width="100%">
												<Box mb={4} px={2}>
													<Stepper index={activeStep} gap="2" colorScheme="orange" size="sm">
														{steps.map((step, index) => (
															<SpecialStep
																key={index}
																opacity={index <= firstInvalidStepIndex ? "1" : "0.6"}
																cursor={index <= firstInvalidStepIndex ? "pointer" : "auto"}
																onClick={() => index <= firstInvalidStepIndex && handleSaveStepClick(index)}
																aria-label={step.label}>
																<StepIndicator>
																	<StepStatus
																		complete={<StepIcon />}
																		incomplete={<StepNumber />}
																		active={<StepNumber />}
																	/>
																</StepIndicator>
																<StepSeparator />
															</SpecialStep>
														))}
													</Stepper>
												</Box>
												<Box flex="1" minWidth="0">
													{activeStep === 0 && (
														<BasicInfo
															handleStepClick={handleStepClick}
															currentStep={activeStep}
															isAttorney={isAttorney}
														/>
													)}
													{activeStep === 1 && (
														<Services
															handleStepPrevious={handlePreviousClick}
															handleStepClick={handleStepClick}
															currentStep={activeStep}
														/>
													)}
													{activeStep === 2 && (
														<AssociateDocs
															handleStepPrevious={handlePreviousClick}
															handleStepClick={handleStepClick}
															currentStep={activeStep}
															hasGeneralAdminAccess={hasGeneralAdminAccess}
														/>
													)}
													{activeStep === 3 && (
														<Billing
															handleStepPrevious={handlePreviousClick}
															handleStepClick={handleStepClick}
															currentStep={activeStep}
														/>
													)}
													{activeStep === 4 && (
														<Confirmation
															handleStepPrevious={handlePreviousClick}
															handleClaimSubmit={handleSubmitClaim}
															handleEditStep={handleEditStep}
															isAttorney={isAttorney}
														/>
													)}
												</Box>
											</Box>
										)}
										{/* Desktop Stepper (Vertical) */}
										{!isMobile && (
											<Box display="flex" flexDirection="row" flex="1">
												<Box
													mr={3}
													maxWidth="150px"
													minWidth="120px"
													flexShrink={0}
													data-testid="new-claim-stepper-wrapper">
													<Stepper
														index={activeStep}
														orientation="vertical"
														colorScheme="orange"
														gap="1"
														m="2px"
														mr={4}
														flexWrap="wrap">
														{steps.map((step, index) => (
															<SpecialStep
																key={index}
																opacity={index <= firstInvalidStepIndex && index <= activeStep ? "1" : "0.6"}
																cursor={index <= firstInvalidStepIndex && index <= activeStep ? "pointer" : "auto"}
																onClick={() =>
																	index <= firstInvalidStepIndex && index <= activeStep && handleSaveStepClick(index)
																}
																aria-label={step.label}
																className="step">
																<StepIndicator>
																	<StepStatus
																		complete={<StepIcon />}
																		incomplete={<StepNumber />}
																		active={<StepNumber />}
																	/>
																</StepIndicator>
																<StepInfoWrapper mt="4px">
																	<StepInfoTitle fontSize="sm">{step.label}</StepInfoTitle>
																	{activeStep === index && (
																		<StepInfoDescription fontSize="xs">{step.description}</StepInfoDescription>
																	)}
																</StepInfoWrapper>
																<StepSeparator />
															</SpecialStep>
														))}
													</Stepper>
												</Box>
												<Box flex="1" minWidth="0" data-testid="new-claim-content-wrapper">
													{activeStep === 0 && (
														<BasicInfo
															handleStepClick={handleStepClick}
															currentStep={activeStep}
															isAttorney={isAttorney}
														/>
													)}
													{activeStep === 1 && (
														<Services
															handleStepPrevious={handlePreviousClick}
															handleStepClick={handleStepClick}
															currentStep={activeStep}
														/>
													)}
													{activeStep === 2 && (
														<AssociateDocs
															handleStepPrevious={handlePreviousClick}
															handleStepClick={handleStepClick}
															currentStep={activeStep}
															hasGeneralAdminAccess={hasGeneralAdminAccess}
														/>
													)}
													{activeStep === 3 && (
														<Billing
															handleStepPrevious={handlePreviousClick}
															handleStepClick={handleStepClick}
															currentStep={activeStep}
														/>
													)}
													{activeStep === 4 && (
														<Confirmation
															handleStepPrevious={handlePreviousClick}
															handleClaimSubmit={handleSubmitClaim}
															handleEditStep={handleEditStep}
															isAttorney={isAttorney}
														/>
													)}
												</Box>
											</Box>
										)}
									</Box>
								</Box>
							</Box>
						</Page>
					</Box>
				</Box>
			</LayoutWrapper>
		</>
	);
};

export default NewClaim;
